import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  ShieldCheck, LayoutDashboard, Building2, Users, ScrollText, KeyRound,
  HardDrive, Gauge, Settings, LogOut, ArrowLeftRight, ChevronDown, Zap, Flame,
  Eye, BellRing, Bot, KeySquare, AlertTriangle, DatabaseBackup,
} from "lucide-react";
import { useSuperAdminAuth } from "../context/SuperAdminAuthContext";
import { useSuperAdminTenant } from "../context/SuperAdminTenantContext";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/superadmin", label: "Overview", icon: LayoutDashboard, feature: null, end: true, testid: "sa-nav-overview" },
  { to: "/superadmin/tenants", label: "Tenants", icon: Building2, feature: "tenants", testid: "sa-nav-tenants" },
  { to: "/superadmin/admins", label: "Super Admins", icon: Users, feature: "admins", testid: "sa-nav-admins" },
  { to: "/superadmin/audit", label: "Audit Logs", icon: ScrollText, feature: "audit", testid: "sa-nav-audit" },
  { to: "/superadmin/login-activity", label: "Login Activity", icon: KeyRound, feature: "login_activity", testid: "sa-nav-logins" },
  { to: "/superadmin/storage", label: "Storage & Cost", icon: HardDrive, feature: "storage", testid: "sa-nav-storage" },
  { to: "/superadmin/usage", label: "Usage", icon: Zap, feature: "usage", testid: "sa-nav-usage" },
  { to: "/superadmin/insights", label: "Insights", icon: Flame, feature: "insights", testid: "sa-nav-insights" },
  { to: "/superadmin/alerts", label: "Alert Rules", icon: BellRing, feature: "alerts", testid: "sa-nav-alerts" },
  { to: "/superadmin/ai-gateway", label: "AI Gateway", icon: Bot, feature: "ai_gateway", testid: "sa-nav-ai-gateway" },
  { to: "/superadmin/performance", label: "Performance", icon: Gauge, feature: "performance", testid: "sa-nav-performance" },
  { to: "/superadmin/sso-keys", label: "SSO Keys", icon: KeySquare, feature: "sso_keys", testid: "sa-nav-sso-keys" },
  { to: "/superadmin/schema-drift", label: "Schema Drift", icon: AlertTriangle, feature: "schema_drift", testid: "sa-nav-schema-drift" },
  { to: "/superadmin/impersonation-log", label: "Session Replay", icon: Eye, feature: null, testid: "sa-nav-timeline" },
  { to: "/superadmin/backups", label: "Database Backup", icon: DatabaseBackup, feature: "backups", testid: "sa-nav-backups" },
  { to: "/superadmin/settings", label: "Global Settings", icon: Settings, feature: "settings", testid: "sa-nav-settings" },
];

function TenantFilter() {
  const { tenants, tenantId, setTenantId, loading } = useSuperAdminTenant();
  const label = tenantId === "all" ? "All Tenants" :
    (tenants.find((t) => t.id === tenantId)?.name || "Tenant");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="sa-tenant-filter"
          className="flex items-center gap-2 px-3 h-9 rounded-xl bg-white/5 border border-white/10 text-sm text-white/90 hover:bg-white/10 transition"
          disabled={loading}
        >
          <Building2 className="w-4 h-4 text-[color:var(--sa-accent,#22D3EE)]" />
          <span className="max-w-[180px] truncate">{label}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[240px] max-h-[420px] overflow-y-auto">
        <DropdownMenuLabel className="text-xs">Filter by tenant</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="sa-tenant-filter-all"
          onClick={() => setTenantId("all")}
          className={cn("cursor-pointer", tenantId === "all" && "font-semibold")}
        >
          All Tenants
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {tenants.map((t) => (
          <DropdownMenuItem
            key={t.id}
            data-testid={`sa-tenant-filter-${t.id}`}
            onClick={() => setTenantId(t.id)}
            className={cn("cursor-pointer flex items-center justify-between gap-3", tenantId === t.id && "font-semibold")}
          >
            <span className="truncate">{t.name}</span>
            <span className="text-[10px] opacity-60 shrink-0">{t.counts?.users || 0}u</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SuperAdminShell({ children, title, actions }) {
  const { sa, logout, has } = useSuperAdminAuth();
  const navigate = useNavigate();
  const initials = (sa?.name || "S").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const visibleNav = NAV.filter((n) => !n.feature || has(n.feature));
  return (
    <div className="sa-portal min-h-screen flex" style={{
      // Distinct visual — deep charcoal + cyan accent to make it visually clear
      // this is the platform cockpit, not the tenant workspace.
      background: "radial-gradient(1200px 600px at 10% -10%, rgba(34,211,238,0.12), transparent 60%), #0B0F14",
      "--sa-accent": "#22D3EE",
      "--sa-accent-2": "#A78BFA",
      color: "#E5E7EB",
    }}>
      <aside className="w-[248px] shrink-0 hidden md:flex flex-col border-r border-white/10 backdrop-blur-md sticky top-0 h-screen"
             style={{ background: "rgba(9,13,18,0.6)" }}>
        <div className="px-5 py-5 flex items-center gap-2.5 shrink-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-lg"
               style={{ background: "linear-gradient(135deg, #22D3EE, #A78BFA)" }}>
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-semibold text-[15px] leading-tight text-white truncate">Super Admin</div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-300/80 leading-tight">Platform Cockpit</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
                isActive
                  ? "bg-cyan-400/10 text-white border-l-2 border-cyan-400"
                  : "text-white/70 hover:bg-white/5 hover:text-white",
              )}
            >
              <n.icon className="w-[18px] h-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 space-y-2">
          <button
            onClick={() => (window.location.href = "/")}
            data-testid="sa-switch-to-client"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-white/80 bg-white/5 hover:bg-white/10 border border-white/10 transition"
          >
            <ArrowLeftRight className="w-4 h-4" />
            Client Portal
          </button>
          <div className="glass-card p-3 flex items-center gap-3 bg-white/5 border-white/10">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="text-white text-xs"
                              style={{ background: "linear-gradient(135deg, #22D3EE, #A78BFA)" }}>
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white truncate">{sa?.name}</div>
              <div className="text-[11px] text-cyan-300 truncate font-medium">
                {sa?.is_master ? "Master" : "Super Admin"}
              </div>
            </div>
            <button data-testid="sa-sidebar-logout" onClick={logout}
                    className="text-white/50 hover:text-red-400 transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 backdrop-blur-md border-b border-white/10"
                style={{ background: "rgba(9,13,18,0.6)" }}>
          <div className="min-h-16 py-2 px-5 md:px-8 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center"
                   style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
                <ShieldCheck className="w-4 h-4 text-white" />
              </div>
              <h1 className="font-display text-xl text-white truncate">{title}</h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <TenantFilter />
              {actions}
              <div className="md:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Avatar className="w-9 h-9 cursor-pointer">
                      <AvatarFallback className="text-white text-xs"
                                      style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>{sa?.email}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/superadmin/admins")}>
                      <Users className="w-4 h-4 mr-2" />Manage Admins
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={logout}>
                      <LogOut className="w-4 h-4 mr-2" />Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 min-h-0 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
